import React from "react";

/**
 * Catches render-time crashes anywhere below it and shows a recoverable
 * fallback UI instead of a blank page. Reports the crash to Sentry
 * if Sentry is loaded (see src/sentry.js).
 *
 * Class component because React error boundaries are class-only — there's
 * no functional component equivalent yet (as of React 19).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    if (typeof window !== "undefined" && window.Sentry) {
      try {
        window.Sentry.captureException(error, { extra: { componentStack: info?.componentStack } });
      } catch (e) {
        console.error("Sentry report failed", e);
      }
    } else {
      // Always log to console so the developer sees something during local dev
      console.error("ErrorBoundary caught:", error, info);
    }
  }

  handleReload = () => {
    // Hard reload — clears in-memory state, keeps sessionStorage
    window.location.reload();
  };

  handleClearAndRestart = () => {
    // Last resort: clear session and reload (sends user to login)
    try {
      sessionStorage.clear();
    } catch {}
    window.location.href = "/";
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <div style={s.root}>
        <div style={s.card}>
          <div style={s.icon}>⚠</div>
          <div style={s.title}>Something went wrong</div>
          <div style={s.message}>
            The page hit an unexpected error and couldn't render. Your data is safe;
            this is a UI issue. Please try reloading.
          </div>
          {this.state.error?.message && (
            <pre style={s.errBox}>{String(this.state.error.message)}</pre>
          )}
          <div style={s.row}>
            <button onClick={this.handleReload} style={s.btnPrimary}>Reload page</button>
            <button onClick={this.handleClearAndRestart} style={s.btnSecondary}>
              Sign out & restart
            </button>
          </div>
          <div style={s.footer}>
            If this keeps happening, contact support@pathriva.health with the time and what
            you were doing.
          </div>
        </div>
      </div>
    );
  }
}

const s = {
  root: {
    minHeight: "100vh",
    background: "#060810",
    color: "#e6edf3",
    fontFamily: "'Inter',-apple-system,sans-serif",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  card: {
    maxWidth: 520,
    background: "#0d1117",
    border: "1px solid #1e2d3d",
    borderRadius: 12,
    padding: 32,
    textAlign: "center",
  },
  icon: { fontSize: 48, marginBottom: 12 },
  title: { fontSize: 22, fontWeight: 800, marginBottom: 12 },
  message: { fontSize: 13, color: "#9aa9b8", lineHeight: 1.6, marginBottom: 16 },
  errBox: {
    background: "#131920",
    border: "1px solid #1e2d3d",
    borderRadius: 6,
    padding: 12,
    color: "#ff4d6d",
    fontSize: 11,
    fontFamily: "monospace",
    textAlign: "left",
    overflowX: "auto",
    marginBottom: 20,
  },
  row: { display: "flex", gap: 12, justifyContent: "center", marginBottom: 16 },
  btnPrimary: {
    background: "linear-gradient(135deg,#00e5ff,#0099cc)",
    border: "none",
    borderRadius: 6,
    color: "#000",
    padding: "10px 20px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  btnSecondary: {
    background: "rgba(255,77,109,0.1)",
    border: "1px solid rgba(255,77,109,0.3)",
    borderRadius: 6,
    color: "#ff4d6d",
    padding: "10px 20px",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  footer: { fontSize: 10, color: "#4a6175" },
};
